/**
 * Utility functions for the Greg James website
 */

export function isTouchDevice() {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  );
}

export function detectBrowser() {
  const userAgent = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/i.test(userAgent) && !window.MSStream;
  const isSafari =
    /safari/i.test(userAgent) && !/chrome|chromium|crios|edg|opr/i.test(userAgent);

  if (isSafari && isIOS) {
    return 'iOS Safari';
  } else if (isSafari) {
    return 'macOS Safari';
  } else if (/chrome|chromium|crios/i.test(userAgent)) {
    return 'Google Chrome';
  } else if (/firefox|fxios/i.test(userAgent)) {
    return 'Mozilla Firefox';
  } else if (/edg/i.test(userAgent)) {
    return 'Microsoft Edge';
  } else if (/opr\//i.test(userAgent)) {
    return 'Opera';
  } else if (/msie|trident/i.test(userAgent)) {
    return 'Microsoft Internet Explorer';
  } else {
    return 'Unknown Browser';
  }
}

export function debounce(func, wait = 20, immediate = true) {
  let timeout;
  return function () {
    const context = this,
      args = arguments;
    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

export function isOverflowing(element) {
  return element.scrollWidth > element.offsetWidth;
}

export function isInternalLink(link) {
  const currentHostname = window.location.hostname;
  const linkHostname = getHostnameFromHref(link.href);

  if (link.href.includes('action')) {
    return false;
  } else {
    return currentHostname === linkHostname;
  }
}

export function getHostnameFromHref(href) {
  if (href) {
    const url = new URL(href);
    return url.hostname;
  } else {
    return false;
  }
}

export function getUrlPathFromHref(href) {
  const link = document.createElement('a');
  link.href = href;
  return link.pathname;
}

export function extractInnerContent(htmlString) {
  const tempElement = document.createElement('div');
  tempElement.innerHTML = htmlString;
  return tempElement;
}

export function isNowBetweenDates(startDateStr, endDateStr) {
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);
  const now = new Date();
  return now >= startDate && now <= endDate;
}

export function getDifferenceInSeconds(dateStr) {
  const targetDate = new Date(dateStr);
  const now = new Date();
  const differenceInMillis = targetDate - now;
  const differenceInSeconds = Math.floor(differenceInMillis / 1000);
  return differenceInSeconds;
}

export function updateURLParameter(url, paramName, paramValue) {
  const newUrl = new URL(url);
  newUrl.searchParams.set(paramName, paramValue);
  return newUrl.toString();
}

export function removeURLParameter(url, paramName) {
  const newUrl = new URL(url);
  newUrl.searchParams.delete(paramName);
  return newUrl.toString();
}

export function removeAllURLParameters(url) {
  const newUrl = new URL(url);
  newUrl.search = '';
  return newUrl.toString();
}

export function getClosestPastTime() {
  const now = new Date();
  const minutes = now.getMinutes();

  let closestMinutes;
  if (minutes < 30) {
    closestMinutes = 0;
  } else {
    closestMinutes = 30;
  }

  const closestTime = new Date(now);
  closestTime.setMinutes(closestMinutes);
  closestTime.setSeconds(0);

  return closestTime.getTime();
}

export function formatSecondsAsTime(seconds) {
  if (seconds < 0) {
    throw new Error('Seconds cannot be negative');
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  } else {
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  }
}

export function formatScheduleStatus(startTimeStr) {
  if (!startTimeStr) return 'Schedule unavailable';

  const startTime = new Date(startTimeStr);
  const now = new Date();
  const diffMs = startTime - now;
  const diffMins = Math.round(diffMs / 60000);

  // In the past
  if (diffMs < 0) {
    const pastMins = Math.abs(diffMins);
    if (pastMins < 60) {
      return `Aired ${pastMins} minute${pastMins !== 1 ? 's' : ''} ago`;
    } else if (pastMins < 1440) {
      const hours = Math.floor(pastMins / 60);
      return `Aired ${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      return `Aired on ${startTime.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}`;
    }
  }

  // In the future
  if (diffMins < 60) {
    return `Airs in ${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
  } else if (diffMins < 1440) {
    const hours = Math.floor(diffMins / 60);
    return `Airs in ${hours} hour${hours !== 1 ? 's' : ''} at ${startTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  } else {
    return `Airs on ${startTime.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} at ${startTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  }
}
